import { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Search, ImagePlus, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FilterSearchInput } from '@/components/shared/FilterSearchInput'
import { useAuthState } from '@/hooks/useAuth'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  filterSearchBarIconButtonClassName,
  filterSearchBarIconButtonSurfaceClassName,
  filterSearchBarIconClassName,
  filterSearchBarShellClassName,
} from '@/components/shared/filterSheetStyles'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'

interface TattooSearchBarProps {
  onSearch: (params: { imageUrl?: string; query?: string; city: string; collections: ('flashes' | 'portfolioItems')[] }) => void
  onClear?: () => void
  selectedCity: string
  collections: ('flashes' | 'portfolioItems')[]
  isSearching?: boolean
  hasActiveSearch?: boolean
}

export interface TattooSearchBarHandle {
  setSearchFromIdea: (imageUrl: string, title: string) => void
}

export const TattooSearchBar = forwardRef<TattooSearchBarHandle, TattooSearchBarProps>(function TattooSearchBar(
  { onSearch, onClear, selectedCity, collections, isSearching = false, hasActiveSearch = false },
  ref
) {
  const { user } = useAuthState()
  const { trackEvent } = useAnalytics()
  const [query, setQuery] = useState('')
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const [selectedImageDownloadUrl, setSelectedImageDownloadUrl] = useState<string | null>(null)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const dismissKeyboard = useCallback(() => {
    searchInputRef.current?.blur()
    if (Capacitor.isNativePlatform()) {
      void Keyboard.hide().catch(() => {})
    }
  }, [])

  const handleImageSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user) return

    setIsUploadingImage(true)
    try {
      // Upload to Firebase Storage at /searchbarUploads/{userId}
      const downloadUrl = await uploadImageToStorageSecure(file, `searchbarUploads/${user.uid}`, user.uid)

      // Create preview URL for display
      const previewUrl = URL.createObjectURL(file)

      setSelectedImageFile(file)
      setSelectedImageUrl(previewUrl) // Use preview for immediate display
      setSelectedImageDownloadUrl(downloadUrl) // Store actual download URL for search

    } catch (error) {
      console.error('Error uploading image:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsUploadingImage(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [user])

  const handleRemoveImage = useCallback(() => {
    setSelectedImageFile(null)
    if (selectedImageUrl && selectedImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImageUrl)
    }
    setSelectedImageUrl(null)
    setSelectedImageDownloadUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [selectedImageUrl])

  const handleSearch = useCallback(() => {
    if (!query.trim() && !selectedImageFile && !selectedImageDownloadUrl) return

    const feedType =
      collections.includes('flashes') && collections.includes('portfolioItems')
        ? 'mixed'
        : collections.includes('flashes')
        ? 'flashes'
        : 'tattoos'

    trackEvent('feed_search', {
      feed_type: feedType,
      query: query.trim() || undefined,
      has_image: Boolean(selectedImageFile || selectedImageDownloadUrl),
      city: selectedCity
    })

    onSearch({
      imageUrl: selectedImageDownloadUrl || undefined,
      query: query.trim() || undefined,
      city: selectedCity,
      collections
    })
    dismissKeyboard()
  }, [query, selectedImageFile, selectedImageDownloadUrl, selectedCity, collections, onSearch, trackEvent, dismissKeyboard])

  const handleClearSearch = useCallback(() => {
    setQuery('')
    handleRemoveImage()
    onClear?.()
  }, [handleRemoveImage, onClear])

  const isSearchDisabled =
    (!query.trim() && !selectedImageFile && !selectedImageDownloadUrl) || isUploadingImage || isSearching

  const hasDraftInput =
    Boolean(query.trim()) || Boolean(selectedImageFile) || Boolean(selectedImageUrl || selectedImageDownloadUrl)

  const showClearButton = Boolean(!isSearching && (hasActiveSearch || hasDraftInput))

  useImperativeHandle(ref, () => ({
    setSearchFromIdea: (imageUrl: string, title: string) => {
      setSelectedImageUrl(imageUrl)
      setSelectedImageDownloadUrl(imageUrl)
      setQuery(title)

      const feedType =
        collections.includes('flashes') && collections.includes('portfolioItems')
          ? 'mixed'
          : collections.includes('flashes')
          ? 'flashes'
          : 'tattoos'

      trackEvent('feed_search', {
        feed_type: feedType,
        query: title,
        has_image: Boolean(imageUrl),
        city: selectedCity
      })

      // Trigger search with the idea
      onSearch({
        imageUrl: imageUrl,
        query: title,
        city: selectedCity,
        collections
      })
      dismissKeyboard()
    }
  }), [selectedCity, collections, onSearch, trackEvent, dismissKeyboard])

  const idleSearchButtonSurface = `${filterSearchBarIconButtonSurfaceClassName} disabled:opacity-50`

  return (
    <div>
      {/* Main search bar */}
      <div className={filterSearchBarShellClassName}>
        {/* Upload button with image preview */}
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${filterSearchBarIconButtonClassName} rounded-l-lg overflow-hidden ${filterSearchBarIconButtonSurfaceClassName}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploadingImage}
        >
          {isUploadingImage ? (
            <Loader2 className={`${filterSearchBarIconClassName} animate-spin`} />
          ) : selectedImageUrl ? (
            <>
              <img
                src={selectedImageUrl}
                alt="Selected"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 hover:bg-black/30 transition-colors" />
            </>
          ) : (
            <ImagePlus className={filterSearchBarIconClassName} />
          )}
        </Button>

        {/* Text input */}
        <FilterSearchInput
          ref={searchInputRef}
          type="text"
          enterKeyHint="done"
          placeholder="style, vibe or motive..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (!isSearchDisabled) handleSearch()
          }}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />

        {/* Search/Clear button */}
        <Button
          variant="ghost"
          size="icon"
          className={
            showClearButton
              ? `${filterSearchBarIconButtonClassName} rounded-r-lg disabled:opacity-50 ${filterSearchBarIconButtonSurfaceClassName}`
              : `${filterSearchBarIconButtonClassName} rounded-r-lg ${idleSearchButtonSurface}`
          }
          onClick={() => {
            if (showClearButton) handleClearSearch()
            else handleSearch()
          }}
          disabled={showClearButton ? isSearching : isSearchDisabled}
        >
          {isSearching ? (
            <Loader2 className={`${filterSearchBarIconClassName} animate-spin`} />
          ) : showClearButton ? (
            <X className={filterSearchBarIconClassName} />
          ) : (
            <Search className={filterSearchBarIconClassName} />
          )}
        </Button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />
      </div>
    </div>
  )
})
